import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PostCalendarGrid, type PostDot } from "../social/PostCalendarGrid";
import { FileText, MessageSquare, MapPin, Bot, Loader2 } from "lucide-react";

const T = {
  navy: "#002868",
  red: "#B22234",
  gold: "#C5A55A",
  border: "rgba(27,27,27,0.12)",
};
const serif = "'Instrument Serif', Georgia, serif";
const sans = "'Instrument Sans', system-ui, sans-serif";

type TypeFilter = "all" | "blog" | "social";

export function ContentCalendarPanel() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");

  // Fetch blog posts with publish_date
  const { data: blogPosts = [], isLoading: blogLoading } = useQuery({
    queryKey: ["content_calendar_blogs", year, month],
    queryFn: async () => {
      const startDate = new Date(year, month, 1).toISOString();
      const endDate = new Date(year, month + 1, 0, 23, 59, 59).toISOString();

      const { data, error } = await supabase
        .from("blog_posts" as any)
        .select("id, title, status, publish_date, state_code, content_type, ai_generated, slug")
        .gte("publish_date", startDate)
        .lte("publish_date", endDate)
        .not("publish_date", "is", null)
        .order("publish_date");

      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  // Fetch social posts with scheduled_at
  const { data: socialPosts = [], isLoading: socialLoading } = useQuery({
    queryKey: ["content_calendar_social", year, month],
    queryFn: async () => {
      const startDate = new Date(year, month, 1).toISOString();
      const endDate = new Date(year, month + 1, 0, 23, 59, 59).toISOString();

      const { data, error } = await supabase
        .from("social_posts" as any)
        .select("id, platform, status, scheduled_at, content, state_code, ai_generated")
        .gte("scheduled_at", startDate)
        .lte("scheduled_at", endDate)
        .not("scheduled_at", "is", null)
        .order("scheduled_at");

      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  // Convert to PostDot format for calendar
  const calendarPosts: PostDot[] = useMemo(() => {
    const items: PostDot[] = [];

    if (typeFilter !== "social") {
      for (const bp of blogPosts) {
        items.push({
          id: bp.id,
          platform: "blog",
          status: bp.status,
          scheduled_at: bp.publish_date,
          type: "blog",
        });
      }
    }

    if (typeFilter !== "blog") {
      for (const sp of socialPosts) {
        items.push({
          id: sp.id,
          platform: sp.platform,
          status: sp.status,
          scheduled_at: sp.scheduled_at,
          type: "social",
        });
      }
    }

    return items;
  }, [blogPosts, socialPosts, typeFilter]);

  // Get items for selected date
  const selectedItems = useMemo(() => {
    if (!selectedDate) return [];

    const items: any[] = [];

    for (const bp of blogPosts) {
      if (!bp.publish_date) continue;
      const d = new Date(bp.publish_date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      if (key === selectedDate && (typeFilter === "all" || typeFilter === "blog")) {
        items.push({ ...bp, _type: "blog" });
      }
    }

    for (const sp of socialPosts) {
      if (!sp.scheduled_at) continue;
      const d = new Date(sp.scheduled_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      if (key === selectedDate && (typeFilter === "all" || typeFilter === "social")) {
        items.push({ ...sp, _type: "social" });
      }
    }

    return items;
  }, [selectedDate, blogPosts, socialPosts, typeFilter]);

  const isLoading = blogLoading || socialLoading;

  return (
    <div>
      {/* Filter bar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12, marginBottom: 20,
      }}>
        {/* Type filter toggle */}
        <div style={{
          display: "flex", gap: 2, backgroundColor: "rgba(0,0,0,0.04)",
          borderRadius: 8, padding: 3,
        }}>
          {([
            { mode: "all" as TypeFilter, label: "All" },
            { mode: "blog" as TypeFilter, label: "Blog Posts" },
            { mode: "social" as TypeFilter, label: "Social Posts" },
          ]).map(({ mode, label }) => (
            <button
              key={mode}
              onClick={() => setTypeFilter(mode)}
              style={{
                padding: "5px 14px", border: "none", borderRadius: 6,
                backgroundColor: typeFilter === mode ? "#fff" : "transparent",
                color: typeFilter === mode ? T.navy : "rgba(27,27,27,0.4)",
                fontSize: 12, fontFamily: sans,
                fontWeight: typeFilter === mode ? 600 : 400,
                cursor: "pointer",
                boxShadow: typeFilter === mode ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Legend */}
        <div style={{ display: "flex", gap: 12, marginLeft: "auto", fontSize: 11, fontFamily: sans, color: "rgba(27,27,27,0.62)" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: T.gold, display: "inline-block" }} />
            Blog
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#1da1f2", display: "inline-block" }} />
            Social
          </span>
        </div>
      </div>

      {isLoading && (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Loader2 size={24} style={{ color: T.navy, animation: "spin 1s linear infinite" }} />
        </div>
      )}

      {!isLoading && (
        <>
          <PostCalendarGrid
            year={year}
            month={month}
            posts={calendarPosts}
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
            onPrevMonth={() => {
              if (month === 0) { setYear(y => y - 1); setMonth(11); }
              else setMonth(m => m - 1);
              setSelectedDate(null);
            }}
            onNextMonth={() => {
              if (month === 11) { setYear(y => y + 1); setMonth(0); }
              else setMonth(m => m + 1);
              setSelectedDate(null);
            }}
          />

          {/* Selected date detail */}
          {selectedDate && selectedItems.length > 0 && (
            <div style={{
              marginTop: 16, padding: 16,
              border: `1px solid ${T.border}`, borderRadius: 8,
              backgroundColor: "#fff",
            }}>
              <h4 style={{
                fontFamily: serif, fontSize: 16, color: T.navy, marginBottom: 12,
              }}>
                {new Date(selectedDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
              </h4>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {selectedItems.map(item => (
                  <div
                    key={item.id}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "8px 12px", borderRadius: 6,
                      backgroundColor: item._type === "blog" ? "rgba(197,165,90,0.06)" : "rgba(0,40,104,0.04)",
                      border: `1px solid ${T.border}`,
                    }}
                  >
                    {item._type === "blog" ? (
                      <FileText size={14} style={{ color: T.gold, flexShrink: 0 }} />
                    ) : (
                      <MessageSquare size={14} style={{ color: T.navy, flexShrink: 0 }} />
                    )}

                    <span style={{
                      padding: "1px 6px", borderRadius: 3, fontSize: 9, fontWeight: 700,
                      fontFamily: sans, textTransform: "uppercase",
                      backgroundColor: item._type === "blog" ? "rgba(197,165,90,0.15)" : "rgba(0,40,104,0.08)",
                      color: item._type === "blog" ? "#8B7730" : T.navy,
                      flexShrink: 0,
                    }}>
                      {item._type === "blog" ? "Blog" : item.platform}
                    </span>

                    <span style={{
                      padding: "1px 6px", borderRadius: 3, fontSize: 9, fontWeight: 600,
                      fontFamily: sans, textTransform: "uppercase",
                      backgroundColor: item.status === "published" ? "rgba(0,40,104,0.08)" :
                                       item.status === "approved" ? "rgba(22,163,74,0.08)" :
                                       "rgba(27,27,27,0.06)",
                      color: item.status === "published" ? T.navy :
                             item.status === "approved" ? "#16a34a" : "#666",
                      flexShrink: 0,
                    }}>
                      {item.status}
                    </span>

                    {item.state_code && (
                      <span style={{
                        display: "flex", alignItems: "center", gap: 2,
                        fontSize: 9, fontWeight: 600, color: "#8B7730", fontFamily: sans,
                      }}>
                        <MapPin size={9} /> {item.state_code}
                      </span>
                    )}

                    <span style={{
                      flex: 1, fontSize: 12, fontFamily: sans, color: "#333",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {item._type === "blog" ? item.title : (item.content?.substring(0, 80) || "Social post")}
                    </span>

                    {item.ai_generated && <Bot size={11} style={{ color: "#7c3aed", flexShrink: 0 }} />}
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedDate && selectedItems.length === 0 && (
            <div style={{
              marginTop: 16, padding: 24, textAlign: "center",
              border: `1px solid ${T.border}`, borderRadius: 8,
              backgroundColor: "#fff", color: "rgba(27,27,27,0.4)",
              fontFamily: sans, fontSize: 13,
            }}>
              No content scheduled for this date
            </div>
          )}
        </>
      )}
    </div>
  );
}
